import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import { getCurrentUser } from "../../utils/userStorage.js";
import authService from "../../services/authService.js";
import "../compnay/ManageCompany.css";
import "./liveDispatchMonitor.css";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "offering", label: "Offering" },
  { value: "openpool", label: "Open pool" },
  { value: "attention", label: "Needs attention" },
  { value: "exhausted", label: "Exhausted / retrying" },
];

const dispatchKey = (compId, invoiceNo) => `${compId}|${String(invoiceNo || "").trim()}`;

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

const formatRiderName = (offer) => {
  const name = pick(offer, "riderName", "RiderName");
  if (name && String(name).trim()) return String(name).trim();
  const id = pick(offer, "marketplaceRider_ID", "MarketplaceRider_ID");
  return id ? `Rider #${id}` : "—";
};

const formatTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const secondsLeft = (expiresAt, nowMs) => {
  if (!expiresAt) return null;
  const end = new Date(expiresAt).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - nowMs) / 1000));
};

const normalizeOffer = (raw) => {
  if (!raw) return null;
  return {
    offerId: pick(raw, "offer_ID", "Offer_ID", "offerId"),
    marketplaceRiderId: pick(raw, "marketplaceRider_ID", "MarketplaceRider_ID", "marketplaceRiderId") || 0,
    riderName: pick(raw, "riderName", "RiderName") || "",
    offerSequence: pick(raw, "offerSequence", "OfferSequence") || 0,
    status: pick(raw, "status", "Status") || "",
    offeredAt: pick(raw, "offeredAt", "OfferedAt"),
    expiresAt: pick(raw, "expiresAt", "ExpiresAt"),
    distanceKm: pick(raw, "distanceKm", "DistanceKm"),
    rankBucket: pick(raw, "rankBucket", "RankBucket"),
  };
};

const normalizeDispatch = (raw) => {
  const currentOffer = normalizeOffer(
    pick(raw, "currentOffer", "CurrentOffer") ||
      (pick(raw, "hasPendingOffer", "HasPendingOffer")
        ? {
            offer_ID: pick(raw, "offer_ID", "Offer_ID"),
            marketplaceRider_ID: pick(raw, "pendingRider_ID", "PendingRider_ID"),
            riderName: pick(raw, "pendingRiderName", "PendingRiderName"),
            offerSequence: pick(raw, "pendingOfferSequence", "PendingOfferSequence"),
            status: pick(raw, "pendingOfferStatus", "PendingOfferStatus") || "Pending",
            offeredAt: pick(raw, "pendingOfferedAt", "PendingOfferedAt"),
            expiresAt: pick(raw, "pendingExpiresAt", "PendingExpiresAt"),
          }
        : null)
  );

  const hasPending =
    Boolean(pick(raw, "hasPendingOffer", "HasPendingOffer")) ||
    String(currentOffer?.status || "").toLowerCase() === "pending";

  return {
    key: dispatchKey(
      pick(raw, "compID", "CompID"),
      pick(raw, "recvorder_InvoiceNo", "Recvorder_InvoiceNo", "invoiceNo")
    ),
    compId: pick(raw, "compID", "CompID") || 0,
    invoiceNo: pick(raw, "recvorder_InvoiceNo", "Recvorder_InvoiceNo", "invoiceNo") || "",
    recvorderId: pick(raw, "recvorder_ID", "Recvorder_ID") || 0,
    status: pick(raw, "recvorder_status", "Recvorder_status") || "",
    compName: pick(raw, "compName", "CompName") || "",
    companySlug: pick(raw, "companySlug", "CompanySlug") || "",
    cycle: pick(raw, "riderDispatchCycle", "RiderDispatchCycle", "cycle") || 0,
    needsAttention: Boolean(
      pick(raw, "riderDispatchNeedsAttention", "RiderDispatchNeedsAttention", "needsAttention")
    ),
    openPool: Boolean(pick(raw, "riderDispatchOpenPool", "RiderDispatchOpenPool", "openPool")),
    exhaustedAt: pick(raw, "riderDispatchExhaustedAt", "RiderDispatchExhaustedAt"),
    lastRetryAt: pick(raw, "riderDispatchLastRetryAt", "RiderDispatchLastRetryAt"),
    hasPendingOffer: hasPending,
    currentOffer,
    offerAttemptCount: pick(raw, "offerAttemptCount", "OfferAttemptCount") || 0,
    boardStatus: hasPending
      ? "offering"
      : Boolean(pick(raw, "riderDispatchOpenPool", "RiderDispatchOpenPool", "openPool"))
        ? "openpool"
        : Boolean(pick(raw, "riderDispatchNeedsAttention", "RiderDispatchNeedsAttention", "needsAttention"))
          ? "attention"
          : pick(raw, "riderDispatchExhaustedAt", "RiderDispatchExhaustedAt")
            ? "exhausted"
            : "waiting",
    closed: false,
    timeline: [],
  };
};

const normalizeTimelineItem = (raw) => ({
  offerId: pick(raw, "offer_ID", "Offer_ID"),
  marketplaceRiderId: pick(raw, "marketplaceRider_ID", "MarketplaceRider_ID") || 0,
  riderName: pick(raw, "riderName", "RiderName") || "",
  offerSequence: pick(raw, "offerSequence", "OfferSequence") || 0,
  status: pick(raw, "status", "Status") || "",
  offeredAt: pick(raw, "offeredAt", "OfferedAt"),
  expiresAt: pick(raw, "expiresAt", "ExpiresAt"),
  createdAt: pick(raw, "createdAt", "CreatedAt"),
});

const resolveAccessToken = () => {
  const fromService = authService.getAccessToken();
  if (fromService) return fromService;
  const fromUserStorage = getCurrentUser()?.token;
  if (fromUserStorage) return fromUserStorage;
  try {
    const sessionUser = sessionStorage.getItem("user");
    if (sessionUser) {
      const parsed = JSON.parse(sessionUser);
      if (parsed?.token) return parsed.token;
    }
  } catch {
    // ignore
  }
  try {
    const localUser = localStorage.getItem("user");
    if (localUser) {
      const parsed = JSON.parse(localUser);
      if (parsed?.token) return parsed.token;
    }
  } catch {
    // ignore
  }
  return "";
};

const getApiBase = () => {
  const url =
    process.env.REACT_APP_Environment === "development"
      ? process.env.REACT_APP_API_URL
      : process.env.REACT_APP_API_URL_Production;
  return String(url || "").replace(/\/+$/, "");
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    showSuccessToast("Copied");
  } catch {
    showErrorToast("Could not copy");
  }
};

const LiveDispatchMonitor = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(true);
  const [hubState, setHubState] = useState("disconnected");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dispatches, setDispatches] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [actionBusy, setActionBusy] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const hubRef = useRef(null);
  const selectedKeyRef = useRef(null);
  const closeTimersRef = useRef({});

  useEffect(() => {
    selectedKeyRef.current = selectedKey;
  }, [selectedKey]);

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const upsertDispatch = useCallback((updater) => {
    setDispatches((prev) => {
      const next = { ...prev };
      updater(next);
      return next;
    });
  }, []);

  const scheduleRemoveClosed = useCallback((key) => {
    if (closeTimersRef.current[key]) clearTimeout(closeTimersRef.current[key]);
    closeTimersRef.current[key] = setTimeout(() => {
      setDispatches((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSelectedKey((cur) => (cur === key ? null : cur));
      delete closeTimersRef.current[key];
    }, 8000);
  }, []);

  const loadTimeline = useCallback(async (compId, invoiceNo, key) => {
    if (!compId || !invoiceNo) return;
    try {
      setTimelineLoading(true);
      const response = await axiosInstance.get(APIs.GET_DISPATCH_TIMELINE, {
        params: { CompID: compId, InvoiceNo: invoiceNo },
      });
      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to load timeline");
        return;
      }
      const rows = Array.isArray(response.data.data) ? response.data.data.map(normalizeTimelineItem) : [];
      upsertDispatch((map) => {
        if (!map[key]) return;
        map[key] = { ...map[key], timeline: rows };
      });
    } catch {
      showErrorToast("Failed to load dispatch timeline");
    } finally {
      setTimelineLoading(false);
    }
  }, [upsertDispatch]);

  const hydrateBoard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ACTIVE_DISPATCHES, {
        params: { Search: search || undefined },
      });
      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to load active dispatches");
        return;
      }
      const rows = Array.isArray(response.data.data) ? response.data.data : [];
      const map = {};
      rows.forEach((row) => {
        const item = normalizeDispatch(row);
        if (item.compId && item.invoiceNo) map[item.key] = item;
      });
      setDispatches(map);
      const keys = Object.keys(map);
      if (keys.length > 0) {
        setSelectedKey((cur) => (cur && map[cur] ? cur : keys[0]));
      } else {
        setSelectedKey(null);
      }
    } catch {
      showErrorToast("Failed to load active dispatches");
    } finally {
      setLoading(false);
    }
  }, [search]);

  const applyMonitorEvent = useCallback(
    (evt) => {
      if (!evt) return;
      const eventType = pick(evt, "eventType", "EventType") || "";
      const compId = pick(evt, "compID", "CompID") || 0;
      const invoiceNo = pick(evt, "recvorder_InvoiceNo", "Recvorder_InvoiceNo", "invoiceNo") || "";
      if (!compId || !invoiceNo) return;

      const key = dispatchKey(compId, invoiceNo);
      const offer = normalizeOffer(pick(evt, "offer", "Offer"));
      const outcomeStatus = pick(evt, "outcomeStatus", "OutcomeStatus");
      const reason = pick(evt, "reason", "Reason");
      const cycle = pick(evt, "cycle", "Cycle");
      const needsAttention = Boolean(pick(evt, "needsAttention", "NeedsAttention"));
      const openPool = Boolean(pick(evt, "openPool", "OpenPool"));
      const eligibleCount = pick(evt, "eligibleCount", "EligibleCount");
      const compName = pick(evt, "compName", "CompName");
      const companySlug = pick(evt, "companySlug", "CompanySlug");
      const timestamp = pick(evt, "timestamp", "Timestamp") || new Date().toISOString();

      upsertDispatch((map) => {
        const existing = map[key] || {
          key,
          compId,
          invoiceNo,
          recvorderId: 0,
          status: "",
          compName: "",
          companySlug: "",
          cycle: 0,
          needsAttention: false,
          openPool: false,
          exhaustedAt: null,
          lastRetryAt: null,
          hasPendingOffer: false,
          currentOffer: null,
          offerAttemptCount: 0,
          boardStatus: "waiting",
          closed: false,
          timeline: [],
        };

        const next = { ...existing };
        if (compName) next.compName = compName;
        if (companySlug) next.companySlug = companySlug;
        if (cycle !== undefined && cycle !== null) next.cycle = cycle;
        next.needsAttention = needsAttention;
        next.openPool = openPool;
        next.closed = false;

        if (eventType === "DispatchStarted") {
          next.boardStatus = "offering";
          next.hasPendingOffer = false;
          if (eligibleCount != null) next.eligibleCount = eligibleCount;
        } else if (eventType === "OfferPresented" && offer) {
          next.hasPendingOffer = true;
          next.currentOffer = offer;
          next.boardStatus = "offering";
          next.offerAttemptCount = Math.max(next.offerAttemptCount || 0, offer.offerSequence || 0);
          const timeline = [...(next.timeline || [])];
          const idx = timeline.findIndex(
            (t) =>
              (offer.offerId && t.offerId === offer.offerId) ||
              (t.offerSequence === offer.offerSequence &&
                t.marketplaceRiderId === offer.marketplaceRiderId)
          );
          const row = {
            offerId: offer.offerId,
            marketplaceRiderId: offer.marketplaceRiderId,
            riderName: offer.riderName,
            offerSequence: offer.offerSequence,
            status: "Pending",
            offeredAt: offer.offeredAt || timestamp,
            expiresAt: offer.expiresAt,
            createdAt: timestamp,
          };
          if (idx >= 0) timeline[idx] = { ...timeline[idx], ...row };
          else timeline.push(row);
          next.timeline = timeline.sort((a, b) => a.offerSequence - b.offerSequence);
        } else if (eventType === "OfferOutcome" && (offer || outcomeStatus)) {
          const status = outcomeStatus || offer?.status || "Declined";
          next.hasPendingOffer = false;
          if (next.currentOffer?.marketplaceRiderId === offer?.marketplaceRiderId) {
            next.currentOffer = { ...next.currentOffer, status };
          }
          if (status.toLowerCase() !== "accepted") {
            next.boardStatus = next.needsAttention ? "attention" : "waiting";
          }
          const timeline = [...(next.timeline || [])];
          const idx = timeline.findIndex(
            (t) =>
              (offer?.offerId && t.offerId === offer.offerId) ||
              (offer &&
                t.offerSequence === offer.offerSequence &&
                t.marketplaceRiderId === offer.marketplaceRiderId) ||
              (offer && t.marketplaceRiderId === offer.marketplaceRiderId && t.status === "Pending")
          );
          if (idx >= 0) {
            timeline[idx] = {
              ...timeline[idx],
              status,
              riderName: offer?.riderName || timeline[idx].riderName,
            };
          } else if (offer) {
            timeline.push({
              offerId: offer.offerId,
              marketplaceRiderId: offer.marketplaceRiderId,
              riderName: offer.riderName,
              offerSequence: offer.offerSequence,
              status,
              offeredAt: offer.offeredAt || timestamp,
              expiresAt: offer.expiresAt,
              createdAt: timestamp,
            });
          }
          next.timeline = timeline.sort((a, b) => a.offerSequence - b.offerSequence);
        } else if (eventType === "DispatchExhausted") {
          next.hasPendingOffer = false;
          next.currentOffer = null;
          next.boardStatus = openPool
            ? "openpool"
            : needsAttention
              ? "attention"
              : "exhausted";
          next.exhaustedAt = timestamp;
          next.reason = reason;
          if (eligibleCount != null) next.eligibleCount = eligibleCount;
        } else if (eventType === "DispatchOpenPool") {
          next.hasPendingOffer = false;
          next.currentOffer = null;
          next.openPool = true;
          next.needsAttention = true;
          next.boardStatus = "openpool";
          next.exhaustedAt = timestamp;
          next.reason = reason || "open_pool";
          if (eligibleCount != null) next.eligibleCount = eligibleCount;
        } else if (eventType === "DispatchRetry") {
          next.boardStatus = "offering";
          next.hasPendingOffer = false;
          next.currentOffer = null;
          next.openPool = false;
          next.lastRetryAt = timestamp;
          next.needsAttention = needsAttention;
        } else if (eventType === "DispatchClosed") {
          next.closed = true;
          next.hasPendingOffer = false;
          next.boardStatus = outcomeStatus === "Accepted" ? "assigned" : "closed";
          if (offer) {
            next.currentOffer = { ...offer, status: outcomeStatus || offer.status };
          }
          next.reason = reason;
          if (String(outcomeStatus || "").toLowerCase() === "cancelled" || reason?.includes("cancel")) {
            next.status = "Cancelled";
          }
        }

        map[key] = next;
      });

      if (eventType === "DispatchClosed") {
        scheduleRemoveClosed(key);
      }
    },
    [scheduleRemoveClosed, upsertDispatch]
  );

  const connectHub = useCallback(() => {
    if (hubRef.current) return;

    const hubPath = String(APIs.HUB_SUPERADMIN_DISPATCH_MONITOR || "").replace(/^\//, "");
    const hubUrl = `${getApiBase()}/${hubPath}`.replace(/([^:]\/)\/+/g, "$1");

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => resolveAccessToken(),
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("DispatchMonitorEvent", (payload) => {
      applyMonitorEvent(payload);
    });

    connection.onreconnecting(() => setHubState("reconnecting"));
    connection.onreconnected(async () => {
      setHubState("connected");
      await hydrateBoard();
    });
    connection.onclose(() => {
      setHubState("disconnected");
      hubRef.current = null;
    });

    hubRef.current = connection;
    setHubState("connecting");
    connection
      .start()
      .then(() => setHubState("connected"))
      .catch((err) => {
        console.warn("SuperAdminDispatchMonitor hub connect failed:", err?.message || err);
        setHubState("disconnected");
        hubRef.current = null;
      });
  }, [applyMonitorEvent, hydrateBoard]);

  useEffect(() => {
    hydrateBoard().then(() => connectHub());
    return () => {
      const connection = hubRef.current;
      hubRef.current = null;
      if (connection) connection.stop().catch(() => {});
      Object.values(closeTimersRef.current).forEach((t) => clearTimeout(t));
      closeTimersRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedKey) return;
    const item = dispatches[selectedKey];
    if (!item) return;
    loadTimeline(item.compId, item.invoiceNo, item.key);
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => {
    const rows = Object.values(dispatches);
    const q = search.trim().toLowerCase();
    return rows
      .filter((row) => {
        if (statusFilter !== "all") {
          if (statusFilter === "offering" && row.boardStatus !== "offering") return false;
          if (statusFilter === "openpool" && row.boardStatus !== "openpool") return false;
          if (statusFilter === "attention" && row.boardStatus !== "attention") return false;
          if (
            statusFilter === "exhausted" &&
            row.boardStatus !== "exhausted" &&
            row.boardStatus !== "waiting"
          )
            return false;
        }
        if (!q) return true;
        return (
          row.invoiceNo.toLowerCase().includes(q) ||
          (row.compName || "").toLowerCase().includes(q) ||
          (row.companySlug || "").toLowerCase().includes(q) ||
          String(row.compId).includes(q)
        );
      })
      .sort((a, b) => {
        const rank = (x) =>
          x.boardStatus === "offering"
            ? 0
            : x.boardStatus === "openpool"
              ? 1
              : x.boardStatus === "attention"
                ? 2
                : x.boardStatus === "exhausted"
                  ? 3
                  : x.closed
                    ? 5
                    : 4;
        return rank(a) - rank(b) || String(b.invoiceNo).localeCompare(String(a.invoiceNo));
      });
  }, [dispatches, search, statusFilter]);

  const selected = selectedKey ? dispatches[selectedKey] : null;
  const canAct = Boolean(selected && !selected.closed);

  const stats = useMemo(() => {
    const all = Object.values(dispatches);
    return {
      total: all.filter((d) => !d.closed).length,
      offering: all.filter((d) => d.boardStatus === "offering").length,
      openpool: all.filter((d) => d.boardStatus === "openpool").length,
      attention: all.filter((d) => d.boardStatus === "attention").length,
      exhausted: all.filter((d) => d.boardStatus === "exhausted" || d.boardStatus === "waiting").length,
    };
  }, [dispatches]);

  const badgeLabel = (row) => {
    if (row.closed && row.boardStatus === "assigned") return "Assigned";
    if (row.closed) return "Closed";
    if (row.boardStatus === "offering") return "Offering";
    if (row.boardStatus === "openpool") return "Open pool";
    if (row.boardStatus === "attention") return "Needs attention";
    if (row.boardStatus === "exhausted") return "Exhausted";
    return "Waiting";
  };

  const countdown = selected?.currentOffer?.status === "Pending"
    ? secondsLeft(selected.currentOffer.expiresAt, nowMs)
    : null;

  const runOpsAction = async (actionKey, api, successFallback) => {
    if (!selected || actionBusy) return;
    try {
      setActionBusy(actionKey);
      const response = await axiosInstance.post(api, {
        CompID: selected.compId,
        Recvorder_InvoiceNo: selected.invoiceNo,
      });
      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Action failed");
        return;
      }
      showSuccessToast(response.data?.message || successFallback);
      await loadTimeline(selected.compId, selected.invoiceNo, selected.key);
      await hydrateBoard();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Action failed");
    } finally {
      setActionBusy("");
    }
  };

  const handleSkipOffer = () =>
    runOpsAction("skip", APIs.POST_SKIP_CURRENT_OFFER, "Skipped current rider");

  const handleForceRetry = () =>
    runOpsAction("retry", APIs.POST_FORCE_DISPATCH_RETRY, "Dispatch retry started");

  const handleCancelOrder = async () => {
    if (!selected || actionBusy) return;
    try {
      setActionBusy("cancel");
      const response = await axiosInstance.post(APIs.POST_CANCEL_ORDER_FROM_DISPATCH, {
        CompID: selected.compId,
        Recvorder_InvoiceNo: selected.invoiceNo,
        Reason: cancelReason.trim() || undefined,
      });
      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to cancel order");
        return;
      }
      showSuccessToast(response.data?.message || "Order cancelled");
      setCancelOpen(false);
      setCancelReason("");
      upsertDispatch((map) => {
        if (!map[selected.key]) return;
        map[selected.key] = {
          ...map[selected.key],
          closed: true,
          boardStatus: "closed",
          hasPendingOffer: false,
          status: "Cancelled",
          reason: cancelReason.trim() || "Cancelled by SuperAdmin",
        };
      });
      scheduleRemoveClosed(selected.key);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to cancel order");
    } finally {
      setActionBusy("");
    }
  };

  if (loading && Object.keys(dispatches).length === 0) {
    return <Preloader />;
  }

  return (
    <div className={`live-dispatch-page manage-company-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 className="page-header mb-1">Live Dispatch Monitor</h1>
          <p className="live-dispatch-desc mb-0">
            Watch sequential rider offers in real time, skip stuck riders, retry exhausted queues, or cancel the order.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className={`live-hub-pill live-hub-${hubState}`}>
            <span className="live-hub-dot" />
            {hubState === "connected"
              ? "Live"
              : hubState === "reconnecting"
                ? "Reconnecting"
                : hubState === "connecting"
                  ? "Connecting"
                  : "Offline"}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => hydrateBoard()}
            disabled={Boolean(actionBusy)}
          >
            Refresh board
          </button>
        </div>
      </div>

      <div className="live-stat-row">
        <button type="button" className={`live-stat-card ${statusFilter === "all" ? "is-active-filter" : ""}`} onClick={() => setStatusFilter("all")}>
          <div className="live-stat-label">Active</div>
          <div className="live-stat-value">{stats.total}</div>
        </button>
        <button type="button" className={`live-stat-card is-offering ${statusFilter === "offering" ? "is-active-filter" : ""}`} onClick={() => setStatusFilter("offering")}>
          <div className="live-stat-label">Offering</div>
          <div className="live-stat-value">{stats.offering}</div>
        </button>
        <button type="button" className={`live-stat-card is-openpool ${statusFilter === "openpool" ? "is-active-filter" : ""}`} onClick={() => setStatusFilter("openpool")}>
          <div className="live-stat-label">Open pool</div>
          <div className="live-stat-value">{stats.openpool}</div>
        </button>
        <button type="button" className={`live-stat-card is-attention ${statusFilter === "attention" ? "is-active-filter" : ""}`} onClick={() => setStatusFilter("attention")}>
          <div className="live-stat-label">Needs attention</div>
          <div className="live-stat-value">{stats.attention}</div>
        </button>
        <button type="button" className={`live-stat-card is-exhausted ${statusFilter === "exhausted" ? "is-active-filter" : ""}`} onClick={() => setStatusFilter("exhausted")}>
          <div className="live-stat-label">Exhausted / wait</div>
          <div className="live-stat-value">{stats.exhausted}</div>
        </button>
      </div>

      <div className="live-toolbar">
        <input
          type="search"
          className="form-control live-search"
          placeholder="Search invoice, restaurant, CompID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") hydrateBoard();
          }}
        />
        <div className="live-filter-chips">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`live-chip ${statusFilter === f.value ? "active" : ""}`}
              style={
                statusFilter === f.value
                  ? { background: themeColor, borderColor: themeColor }
                  : undefined
              }
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="live-board">
        <div className="live-board-list">
          {list.length === 0 ? (
            <div className="live-empty">No orders broadcasting.</div>
          ) : (
            list.map((row) => {
              const left =
                row.currentOffer?.status === "Pending"
                  ? secondsLeft(row.currentOffer.expiresAt, nowMs)
                  : null;
              return (
                <button
                  key={row.key}
                  type="button"
                  className={`live-order-card ${selectedKey === row.key ? "selected" : ""} status-${row.boardStatus}`}
                  onClick={() => setSelectedKey(row.key)}
                >
                  <div className="live-order-card-top">
                    <strong>#{row.invoiceNo}</strong>
                    <span className={`live-badge badge-${row.boardStatus}`}>{badgeLabel(row)}</span>
                  </div>
                  <div className="live-order-meta">{row.compName || `Comp ${row.compId}`}</div>
                  <div className="live-order-offer">
                    {row.hasPendingOffer && row.currentOffer ? (
                      <>
                        Offering <strong>{formatRiderName(row.currentOffer)}</strong>
                        {left != null ? ` · ${left}s` : ""}
                      </>
                    ) : row.closed ? (
                      row.boardStatus === "assigned" ? "Assigned" : "Closed"
                    ) : (
                      "Waiting for next rider…"
                    )}
                  </div>
                  <div className="live-order-cycle">
                    Cycle {row.cycle || 0}
                    {row.offerAttemptCount ? ` · ${row.offerAttemptCount} offer(s)` : ""}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="live-board-detail">
          {!selected ? (
            <div className="live-empty">Select an order to see the offer cascade and ops actions.</div>
          ) : (
            <>
              <div className="live-detail-header">
                <div>
                  <h3 className="mb-1">#{selected.invoiceNo}</h3>
                  <div className="live-order-meta">
                    {selected.compName || `Company ${selected.compId}`}
                    {selected.companySlug ? ` · ${selected.companySlug}` : ""}
                    {selected.status ? ` · ${selected.status}` : ""}
                  </div>
                </div>
                <span className={`live-badge badge-${selected.boardStatus}`}>
                  {badgeLabel(selected)}
                </span>
              </div>

              <div className="live-meta-grid">
                <div className="live-meta-item">
                  <span className="live-meta-label">CompID</span>
                  <span className="live-meta-value">{selected.compId}</span>
                </div>
                <div className="live-meta-item">
                  <span className="live-meta-label">Cycle</span>
                  <span className="live-meta-value">{selected.cycle || 0}</span>
                </div>
                <div className="live-meta-item">
                  <span className="live-meta-label">Offers</span>
                  <span className="live-meta-value">{selected.offerAttemptCount || 0}</span>
                </div>
                <div className="live-meta-item">
                  <span className="live-meta-label">Eligible</span>
                  <span className="live-meta-value">{selected.eligibleCount ?? "—"}</span>
                </div>
              </div>

              <div className="live-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => copyText(selected.invoiceNo)}
                >
                  Copy invoice
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => loadTimeline(selected.compId, selected.invoiceNo, selected.key)}
                  disabled={timelineLoading || Boolean(actionBusy)}
                >
                  Refresh timeline
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleSkipOffer}
                  disabled={!canAct || !selected.hasPendingOffer || Boolean(actionBusy)}
                  title="Expire current pending offer and offer the next ranked rider"
                >
                  {actionBusy === "skip" ? "Skipping…" : "Skip rider"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  onClick={handleForceRetry}
                  disabled={!canAct || Boolean(actionBusy)}
                  title="Reset tried riders and start a fresh dispatch pass"
                >
                  {actionBusy === "retry" ? "Retrying…" : "Force retry"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => {
                    setCancelReason("");
                    setCancelOpen(true);
                  }}
                  disabled={!canAct || Boolean(actionBusy)}
                >
                  Cancel order
                </button>
              </div>

              {selected.hasPendingOffer && selected.currentOffer && (
                <div className="live-now-card" style={{ borderColor: themeColor }}>
                  <div className="live-now-label">Offering now</div>
                  <div className="live-now-rider">{formatRiderName(selected.currentOffer)}</div>
                  <div className="live-now-meta">
                    Seq #{selected.currentOffer.offerSequence || "—"}
                    {selected.currentOffer.distanceKm != null
                      ? ` · ${selected.currentOffer.distanceKm} km`
                      : ""}
                    {selected.currentOffer.rankBucket
                      ? ` · ${selected.currentOffer.rankBucket}`
                      : ""}
                    {countdown != null ? ` · ${countdown}s left` : ""}
                  </div>
                </div>
              )}

              {(selected.reason || selected.needsAttention || selected.openPool) && (
                <div className={`live-alert-strip ${selected.needsAttention || selected.openPool ? "is-attention" : ""}`}>
                  {selected.openPool
                    ? "Open pool · eligible riders can accept from Available. "
                    : selected.needsAttention
                      ? "Needs attention · "
                      : ""}
                  {selected.reason || "Waiting for eligible riders or next retry cycle."}
                </div>
              )}

              <div className="live-timeline-title">
                Offer cascade
                {timelineLoading ? " · loading…" : ""}
              </div>
              {(selected.timeline || []).length === 0 ? (
                <div className="live-empty small">No offer history yet for this order.</div>
              ) : (
                <ol className="live-timeline">
                  {(selected.timeline || []).map((step) => {
                    const isPending = String(step.status).toLowerCase() === "pending";
                    const left = isPending ? secondsLeft(step.expiresAt, nowMs) : null;
                    return (
                      <li
                        key={`${step.offerId || step.offerSequence}-${step.marketplaceRiderId}`}
                        className={`live-timeline-item status-${String(step.status || "").toLowerCase()}`}
                      >
                        <div className="live-timeline-dot" />
                        <div className="live-timeline-body">
                          <div className="live-timeline-main">
                            <strong>{formatRiderName(step)}</strong>
                            <span className="live-timeline-status">{step.status}</span>
                          </div>
                          <div className="live-timeline-sub">
                            Seq #{step.offerSequence}
                            {" · "}
                            {formatTime(step.offeredAt)}
                            {left != null ? ` · ${left}s left` : ""}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </>
          )}
        </div>
      </div>

      <CustomModal
        isOpen={cancelOpen}
        onClose={() => {
          if (actionBusy === "cancel") return;
          setCancelOpen(false);
        }}
        title="Cancel Go Fatafat order"
        subtitle={selected ? `#${selected.invoiceNo} · ${selected.compName || `Comp ${selected.compId}`}` : ""}
        isCentered
        showButton
        showSubmitButton
        showCancelButton
        submitButtonText={actionBusy === "cancel" ? "Cancelling…" : "Cancel order"}
        submitButtonDisabled={actionBusy === "cancel"}
        onSubmit={handleCancelOrder}
      >
        <p className="live-cancel-help mb-2">
          This cancels the order, clears any pending rider offers, and notifies connected riders / restaurant.
        </p>
        <label className="form-label" htmlFor="live-cancel-reason">
          Reason (optional)
        </label>
        <textarea
          id="live-cancel-reason"
          className="form-control"
          rows={3}
          maxLength={500}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="e.g. No riders available / customer request / restaurant closed"
        />
      </CustomModal>
    </div>
  );
};

export default LiveDispatchMonitor;
